import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api, finishSignIn, signIn, signOut, token } from "./auth";
import {
  coverageByDomain,
  filterSpecs,
  groupBySpec,
  summarizeCoverage,
  type SpecGroup,
} from "./coverage";
import { attentionItems, isHealthy } from "./home";
import { journeyStatus, journeyStepStatus } from "./journey";
import { parseMarkdown, type Span } from "./markdown";
import {
  filterRequirements,
  groupByFamily,
  requirementStatus,
  requirementStatusLabel,
  resolveDeclaredEvidence,
  summarizeRequirements,
} from "./requirements";
import { statusLabel } from "./status";
import type {
  BillingApproval,
  BillingFlow,
  BillingRequirement,
  BusinessJourney,
  EvidenceStatus,
  Overview,
  RequirementEvidenceStatus,
  RunSummary,
  TestEvidence,
} from "./types";
import "./styles.css";

type View = "home" | "flows" | "assurance" | "codes" | "tests" | "request";

const viewMeta: Record<View, { label: string; hint: string }> = {
  home: { label: "Overview", hint: "Is billing working right now?" },
  flows: { label: "How billing works", hint: "The path a charge takes, stage by stage" },
  assurance: { label: "Billing assurance", hint: "The focused claim journey, step by step" },
  codes: { label: "Charge codes", hint: "Every reviewed code and its evidence" },
  tests: { label: "What we test", hint: "What CI runs, and what it does not" },
  request: { label: "Request a billing test", hint: "Turn a billing gap into work" },
};

const emptyOverview: Overview = { counts: {}, domains: [], recentRuns: [] };
const FOCUS_JOURNEY_ID = "JOURNEY-RPM-BP-TO-BILLING";
const PLATFORM_REPO_URL = "https://git.bettrai.dev/BettrAI/bettr-ai-platform";

function StatusPill({
  status,
  label: labelOverride,
}: {
  status: EvidenceStatus | RequirementEvidenceStatus | "certified" | "covered" | "gap";
  label?: string;
}) {
  const label = labelOverride ?? (status in statusLabel ? statusLabel[status as EvidenceStatus] : status);
  return <span className={`status status-${status}`}>{label}</span>;
}

function sourceUrl(path: string, sha?: string): string {
  const cleanPath = path.split(":", 1)[0];
  const revision = sha ? `commit/${sha}` : "branch/main";
  return `${PLATFORM_REPO_URL}/src/${revision}/${cleanPath}`;
}

/** The recorded Playwright report for the run that produced this result. */
function reportFor(test: TestEvidence, runs: RunSummary[]): RunSummary | undefined {
  return runs.find((run) => run.sha === test.lastRunSha && Boolean(run.artifactUrl));
}

/** `refs/pull/5383/head` → `PR #5383`; anything else stays as written. */
function refLabel(ref?: string): string {
  if (!ref) return "unknown ref";
  const pull = /^refs\/pull\/(\d+)\//.exec(ref);
  if (pull) return `PR #${pull[1]}`;
  return ref.replace(/^refs\/heads\//, "");
}

function whenLabel(iso?: string): string {
  if (!iso) return "";
  const at = new Date(iso);
  return Number.isNaN(at.valueOf())
    ? ""
    : at.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Where the board's verdict comes from.
 *
 * A green header is only a statement about the product when the evidence came
 * from the protected branch. Evidence from an unmerged branch describes code
 * nobody is running, so it is labelled rather than presented identically.
 */
function BuildProvenance({ overview, compact }: { overview: Overview; compact?: boolean }) {
  if (!overview.exactSha) {
    return <span className={`provenance provenance-empty${compact ? " provenance-compact" : ""}`}>
      <span className="provenance-dot" />Awaiting a published run
    </span>;
  }
  const main = overview.headlineIsMain !== false;
  return <span className={`provenance provenance-${main ? "main" : "branch"}${compact ? " provenance-compact" : ""}`}>
    <span className="provenance-dot" />
    <strong>{main ? "main" : refLabel(overview.exactRef)}</strong>
    <code>{overview.exactSha.slice(0, 10)}</code>
    {!compact && <em>{main ? "shipped code" : "not merged"}</em>}
    {!compact && overview.exactRunAt && <small>{whenLabel(overview.exactRunAt)}</small>}
  </span>;
}

/** Proportional tiers so a spec's coverage shape reads without counting rows. */
function CoverageBar({ spec }: { spec: SpecGroup }) {
  const parts: Array<[string, number]> = [
    ["verified", spec.verified],
    ["earlier", spec.earlier],
    ["excluded", spec.excluded],
    ["never", spec.never],
  ];
  return <div className="coverage-bar" aria-hidden="true">
    {parts.filter(([, value]) => value > 0).map(([name, value]) =>
      <span className={`bar-${name}`} key={name} style={{ flexGrow: value }} />)}
  </div>;
}

/** One test inside a spec: its verdict, its named checkpoints, and its source. */
function TestDetail({ test, runs, exactSha }: { test: TestEvidence; runs: RunSummary[]; exactSha?: string }) {
  const report = reportFor(test, runs);
  const steps = test.steps ?? [];
  return <div className={`spec-test spec-test-${test.status}`}>
    <div className="spec-test-head">
      <h4>{test.title.split(" › ").slice(1).join(" › ") || test.title}</h4>
      <StatusPill status={test.status} label={test.status === "missing" ? "Never run" : undefined} />
    </div>
    {steps.length > 0 && <ol className="step-list">
      {steps.map((step) => <li className={`step step-${step.status}`} key={step.index}>
        <span className="step-mark" aria-hidden="true">{step.status === "passed" ? "✓" : step.status === "failed" ? "✕" : "–"}</span>
        <span className="step-title">{step.title}</span>
        <span className="step-time">{(step.durationMs / 1000).toFixed(1)}s</span>
      </li>)}
    </ol>}
    <div className="source-links">
      <a href={sourceUrl(test.path, test.lastRunSha ?? exactSha)} target="_blank" rel="noreferrer">Open the test source</a>
      {report && <a href={report.artifactUrl} target="_blank" rel="noreferrer">Watch the recorded run</a>}
      <span>{test.lastRunSha ? `last run ${test.lastRunSha.slice(0, 10)}` : "no CI run has ever executed this test"}</span>
      {test.durationMs != null && <span>{(test.durationMs / 1000).toFixed(1)}s</span>}
    </div>
  </div>;
}

// Named for the billing outcome, not for the device that happens to generate
// the qualifying data. QA reads this screen to answer "did we charge correctly",
// and leading with the clinical fixture buried that.
function assuranceCopy(status: EvidenceStatus): { eyebrow: string; title: string; message: string } {
  if (status === "passed") return {
    eyebrow: "Billing is behaving correctly",
    title: "Qualifying activity produced the correct charge",
    message: "On this build the engine selected the right code, created the claim exactly once, and exported the same billing facts it stored.",
  };
  if (["failed", "flaky", "stale"].includes(status)) return {
    eyebrow: "Billing needs attention",
    title: "The charge produced by this journey is not trustworthy",
    message: "At least one part of the billing outcome failed or is no longer current. Do not treat this build as billing-ready.",
  };
  return {
    eyebrow: "Billing proof is incomplete",
    title: "This build has not proven the charge is correct",
    message: "The focused billing test has no complete result here. Missing evidence is never treated as a pass.",
  };
}

function BillingAssuranceView({ journeys, tests, requirements, runs, overview, onRequestTest }: {
  journeys: BusinessJourney[];
  tests: TestEvidence[];
  requirements: BillingRequirement[];
  runs: RunSummary[];
  overview: Overview;
  onRequestTest: () => void;
}) {
  const exactSha = overview.exactSha;
  const journey = journeys.find((item) => item.id === FOCUS_JOURNEY_ID) ?? journeys[0];
  if (!journey) return <section><div className="panel empty">The focused billing journey has not been published yet.</div></section>;

  const testsById = new Map(tests.map((test) => [test.id, test]));
  const requirementsById = new Map(requirements.map((item) => [item.id, item]));
  const linkedTestIds = Array.from(new Set(journey.steps.flatMap((step) => step.evidenceTestIds)));
  const linkedTests = linkedTestIds.map((id) => testsById.get(id)).filter((test): test is TestEvidence => Boolean(test));
  const status = journeyStatus(journey, testsById);
  const copy = assuranceCopy(status);

  return <section className="billing-assurance">
    <div className={`assurance-hero assurance-hero-${status}`}>
      <div className="assurance-answer">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1>{copy.title}</h1>
        <p>{copy.message}</p>
        <div className="assurance-meta"><StatusPill status={status} label={status === "missing" ? "Not run" : undefined} /><BuildProvenance overview={overview} /></div>
      </div>
      <div className="single-test-count"><strong>{linkedTestIds.length}</strong><span>focused Playwright test</span><small>with {journey.steps.length} business checkpoints</small></div>
    </div>

    {overview.exactSha && overview.headlineIsMain === false && <div className="branch-warning">
      <strong>This verdict is about an unmerged branch</strong>
      <span>The newest evidence the Hub holds came from {refLabel(overview.exactRef)}, not <code>main</code>. It says nothing about the code running in production.</span>
    </div>}

    <div className="plain-scenario" aria-label="Billing test scenario">
      <article><span>Qualifying activity</span><p>A synthetic patient reaches <strong>16 distinct device-data days</strong> in the billing period, with the setup and education evidence RPM requires. (The device happens to be a blood-pressure cuff — that is fixture detail, not the rule.)</p></article>
      <article><span>Charge selection</span><p>The billing engine must pick the <strong>full-period code 99454</strong> and suppress the mutually exclusive short-period <strong>99445</strong>, then create that claim <strong>exactly once</strong> even if generation is run again.</p></article>
      <article><span>Money out the door</span><p>The exported CSV row must carry the <strong>same claim ID, code, period and integer-cent amount</strong> as the stored claim. This is the number that gets billed.</p></article>
    </div>

    <div className="ground-truth-callout"><span>Most important rule</span><p>{journey.importantNote}</p></div>

    <div className="focused-layout">
      <div>
        <div className="focused-heading"><div><p className="eyebrow">What the test proves</p><h2>One journey, four outcomes</h2></div><span>Read top to bottom</span></div>
        <div className="focused-checkpoints">
          {journey.steps.map((step, index) => {
            const stepStatus = journeyStepStatus(step, testsById);
            const codes = step.requirementIds.map((id) => requirementsById.get(id)?.code ?? id);
            const observed = step.evidenceStepTitle
              ? step.evidenceTestIds
                  .map((id) => testsById.get(id)?.steps?.find((entry) => entry.title === step.evidenceStepTitle))
                  .find(Boolean)
              : undefined;
            return <article className={`focused-checkpoint focused-checkpoint-${stepStatus}`} key={step.id}>
              <span className="focused-number">{index + 1}</span>
              <div>
                <small>{step.stage}{codes.length > 0 ? ` · ${codes.join(" / ")}` : ""}</small>
                <h3>{step.title}</h3>
                <p>{step.expected}</p>
                {observed && <small className="checkpoint-observed">Measured directly · {(observed.durationMs / 1000).toFixed(1)}s</small>}
              </div>
              <StatusPill status={stepStatus} label={stepStatus === "missing" ? "Not run" : undefined} />
            </article>;
          })}
        </div>
      </div>

      <aside className="proof-card panel">
        <p className="eyebrow">The automated proof</p>
        {linkedTests.map((test) => {
          const report = reportFor(test, runs);
          return <div className="focused-test" key={test.id}>
            <StatusPill status={test.status} label={test.status === "missing" ? "Not run" : undefined} />
            <h3>{test.title.split(" › ").at(-1)}</h3>
            <p>Playwright drives the real browser and API across setup, qualification, claim generation, duplicate protection, and CSV reconciliation.</p>
            <a href={sourceUrl(test.path, test.lastRunSha ?? exactSha)} target="_blank" rel="noreferrer">Open the test source</a>
            {report && <a href={report.artifactUrl} target="_blank" rel="noreferrer">Watch the recorded run</a>}
            <small>{test.lastRunSha ? <>Last run <code>{test.lastRunSha.slice(0, 10)}</code>{test.durationMs != null ? ` · ${(test.durationMs / 1000).toFixed(1)}s` : ""}</> : "No exact-build run yet"}</small>
          </div>;
        })}
        {linkedTests.length === 0 && <div className="coverage-gap"><strong>Focused test missing</strong><span>The expected Playwright test is not in the published catalog.</span></div>}
        {status !== "passed" && <button className="secondary proof-request" type="button" onClick={onRequestTest}>Request a billing test change</button>}
      </aside>
    </div>
  </section>;
}

/** Render the flow-document token tree as elements — never as HTML. */
function Markdown({ source }: { source: string }) {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  const spans = (list: Span[]) => list.map((span, index) =>
    span.kind === "strong" ? <strong key={index}>{span.text}</strong>
      : span.kind === "code" ? <code key={index}>{span.text}</code>
        : <span key={index}>{span.text}</span>);
  return <div className="doc">
    {blocks.map((block, index) => {
      if (block.kind === "heading") {
        return block.level === 2 ? <h3 key={index}>{spans(block.spans)}</h3> : <h4 key={index}>{spans(block.spans)}</h4>;
      }
      if (block.kind === "paragraph") return <p key={index}>{spans(block.spans)}</p>;
      if (block.kind === "list") {
        return <ul key={index}>{block.items.map((item, row) => <li key={row}>{spans(item)}</li>)}</ul>;
      }
      if (block.kind === "code") return <pre key={index}><code>{block.text}</code></pre>;
      return <div className="doc-table-scroll" key={index}>
        <table>
          <thead><tr>{block.headers.map((cell, column) => <th key={column}>{spans(cell)}</th>)}</tr></thead>
          <tbody>
            {block.rows.map((row, rowIndex) =>
              <tr key={rowIndex}>{row.map((cell, column) => <td key={column}>{spans(cell)}</td>)}</tr>)}
          </tbody>
        </table>
      </div>;
    })}
  </div>;
}

function HowBillingWorksView({ flows, requirements, onOpenCodes }: {
  flows: BillingFlow[];
  requirements: BillingRequirement[];
  onOpenCodes: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const byId = useMemo(() => new Map(requirements.map((item) => [item.id, item])), [requirements]);
  const active = flows.find((flow) => flow.id === openId) ?? flows[0];

  if (flows.length === 0) {
    return <section>
      <div className="section-heading"><div><p className="eyebrow">Reference</p><h2>How billing works</h2></div></div>
      <div className="panel empty">The billing flow documentation has not been published yet. It ships from the product repository with the code it describes.</div>
    </section>;
  }

  return <section className="flows">
    <div className="section-heading test-heading">
      <div>
        <p className="eyebrow">Reference</p>
        <h2>How billing works</h2>
        <p className="lede">The path a charge takes, stage by stage, from an enrolled patient to a row in the export. This is versioned ground truth in the product repository — it moves in the same commit as the code it describes, and every stage names the source files that implement it.</p>
      </div>
    </div>

    <div className="flow-rail">
      {flows.map((flow, index) => <button
        className={`flow-step${flow.id === active?.id ? " active" : ""}`}
        key={flow.id}
        type="button"
        onClick={() => setOpenId(flow.id)}
      >
        <span className="flow-num">{index + 1}</span>
        <span className="flow-step-title">{flow.title}</span>
      </button>)}
    </div>

    {active && <div className="flow-body">
      <div className="panel flow-doc">
        <div className="flow-doc-head">
          <p className="eyebrow">Stage {flows.indexOf(active) + 1} of {flows.length}</p>
          <h3>{active.title}</h3>
          <p className="flow-summary">{active.summary}</p>
        </div>
        <Markdown source={active.markdown} />
      </div>

      <aside className="flow-side">
        {active.requirementIds.length > 0 && <div className="panel flow-aside">
          <p className="eyebrow">Charge codes this stage governs</p>
          <ul className="flow-codes">
            {active.requirementIds.map((id) => {
              const item = byId.get(id);
              const status = item ? requirementStatus(item) : undefined;
              return <li key={id}>
                <code>{item?.code ?? id}</code>
                {status ? <StatusPill status={status} label={requirementStatusLabel[status]} /> : <small>not in the registry</small>}
              </li>;
            })}
          </ul>
          <button className="secondary flow-codes-link" type="button" onClick={onOpenCodes}>Open the charge-code board</button>
        </div>}

        {active.sourceRefs.length > 0 && <div className="panel flow-aside">
          <p className="eyebrow">Implemented in</p>
          <ul className="flow-sources">
            {active.sourceRefs.map((ref) => <li key={ref}>
              <a href={sourceUrl(ref)} target="_blank" rel="noreferrer">{ref.split("/").slice(-1)[0]}</a>
              <small>{ref}</small>
            </li>)}
          </ul>
        </div>}
      </aside>
    </div>}
  </section>;
}

function HomeView({ requirements, tests, journeys, overview, onOpen }: {
  requirements: BillingRequirement[];
  tests: TestEvidence[];
  journeys: BusinessJourney[];
  overview: Overview;
  onOpen: (view: View) => void;
}) {
  const exactSha = overview.exactSha;
  const browserTests = useMemo(
    () => tests.filter((test) => (test.testType ?? "playwright") === "playwright"),
    [tests],
  );
  const codes = useMemo(() => summarizeRequirements(requirements), [requirements]);
  const coverage = useMemo(() => summarizeCoverage(browserTests, exactSha), [browserTests, exactSha]);
  const attention = useMemo(
    () => attentionItems(requirements, browserTests, exactSha),
    [requirements, browserTests, exactSha],
  );
  const healthy = isHealthy(attention);

  const journey = journeys.find((item) => item.id === FOCUS_JOURNEY_ID) ?? journeys[0];
  const testsById = useMemo(() => new Map(tests.map((test) => [test.id, test])), [tests]);
  const journeyState = journey ? journeyStatus(journey, testsById) : "missing";

  const headline = !overview.exactSha
    ? { eyebrow: "No evidence yet", title: "Nothing has been published to the Hub" }
    : healthy
      ? { eyebrow: "Billing automation is not reporting a failure", title: `${codes.proven} of ${codes.total} charge codes are proven on this build` }
      : { eyebrow: "Billing needs attention", title: "Automation is reporting a billing failure on this build" };

  return <section className="home">
    <div className={`home-hero home-hero-${healthy ? "ok" : "attention"}`}>
      <div>
        <p className="eyebrow">{headline.eyebrow}</p>
        <h1>{headline.title}</h1>
        <p className="home-sub">Everything below is measured against one build. A charge code counts as proven only when a test that claims it passed on that exact build — a reviewed rule with no passing test is not proof.</p>
        <div className="assurance-meta"><BuildProvenance overview={overview} /></div>
      </div>
    </div>

    <div className="home-cards">
      <button className="home-card" type="button" onClick={() => onOpen("codes")}>
        <p className="eyebrow">Billing conformance</p>
        <strong>{codes.proven}<span>/{codes.total}</span></strong>
        <small>charge codes proven on this build</small>
        <div className="home-card-facts">
          {codes.attention > 0 && <b className="fact-bad">{codes.attention} need attention</b>}
          {codes.unlinked > 0 && <b className="fact-gap">{codes.unlinked} untested</b>}
          {codes.openGaps > 0 && <b className="fact-gap">{codes.openGaps} open rule gaps</b>}
          {codes.attention === 0 && codes.unlinked === 0 && codes.openGaps === 0 && <b>every reviewed code is covered</b>}
        </div>
      </button>

      <button className="home-card" type="button" onClick={() => onOpen("tests")}>
        <p className="eyebrow">Browser coverage</p>
        <strong>{coverage.specsExercised}<span>/{coverage.specs}</span></strong>
        <small>spec files CI actually runs</small>
        <div className="home-card-facts">
          <b>{coverage.verified} tests verified on this build</b>
          {coverage.never > 0 && <b className="fact-gap">{coverage.never} never run</b>}
          {coverage.failing > 0 && <b className="fact-bad">{coverage.failing} failing or flaky</b>}
        </div>
      </button>

      <button className="home-card" type="button" onClick={() => onOpen("assurance")}>
        <p className="eyebrow">Focused claim journey</p>
        <strong className="home-card-status"><StatusPill status={journeyState} label={journeyState === "missing" ? "Not run" : undefined} /></strong>
        <small>device days → 99454 → one claim → matching export</small>
        <div className="home-card-facts">
          <b>{journey ? `${journey.steps.length} business checkpoints` : "not published"}</b>
        </div>
      </button>
    </div>

    <div className="section-heading home-heading">
      <div>
        <p className="eyebrow">Ranked by what to do first</p>
        <h2>{attention.length === 0 ? "Nothing is asking for attention" : "What needs attention"}</h2>
      </div>
    </div>

    {attention.length === 0
      ? <div className="panel empty">Every reviewed charge code and every test CI runs passed on this build.</div>
      : <div className="attention-list">
          {attention.map((item) => <button className={`attention attention-${item.severity}`} key={item.id} type="button" onClick={() => onOpen(item.target === "assurance" ? "assurance" : item.target)}>
            <span className="attention-tag">{item.severity === "broken" ? "Failing now" : item.severity === "ageing" ? "Out of date" : "No evidence"}</span>
            <div><strong>{item.headline}</strong><small>{item.detail}</small></div>
            <span className="attention-go" aria-hidden="true">→</span>
          </button>)}
        </div>}
  </section>;
}

function ChargeCodeBoardView({ requirements, approval, tests, overview }: {
  requirements: BillingRequirement[];
  approval: BillingApproval;
  tests: TestEvidence[];
  overview: Overview;
}) {
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState("all");
  const families = useMemo(
    () => Array.from(new Set(requirements.map((item) => item.family))).sort(),
    [requirements],
  );
  const filtered = useMemo(() => filterRequirements(requirements, query, family), [requirements, query, family]);
  const summary = useMemo(() => summarizeRequirements(requirements), [requirements]);
  const groups = useMemo(() => groupByFamily(filtered), [filtered]);
  const rollup = useMemo(() => groupByFamily(requirements), [requirements]);

  return <section>
    <div className="section-heading test-heading">
      <div>
        <p className="eyebrow">Ground truth</p>
        <h2>Every reviewed charge code</h2>
        <p className="lede">Two separate facts per code. <strong>Reviewed</strong> is the billing specialist's judgement of the rule. <strong>Proven</strong> is what automation observed on this build. A code is only safe to bill when both are green — a certified rule with no passing test has been read by a human, not demonstrated by the software.</p>
      </div>
      <div className="approval-note">{approval.status === "approved" ? `BRD approved by ${approval.owners.join(" + ")}` : `BRD review still required from ${approval.owners.join(" + ")}`}</div>
    </div>

    <div className="workflow-summary">
      <div className="summary-card summary-proven"><span>Proven on this build</span><strong>{summary.proven}</strong><small>of {summary.total} reviewed codes</small></div>
      <div className="summary-card summary-attention"><span>Needs attention</span><strong>{summary.attention}</strong><small>failing, flaky, or stale</small></div>
      <div className="summary-card summary-gap"><span>No automated test</span><strong>{summary.unlinked}</strong><small>no test claims this code</small></div>
      <div className="summary-card summary-gap"><span>Open rule gaps</span><strong>{summary.openGaps}</strong><small>reviewed as incomplete</small></div>
    </div>

    <div className="family-rollup">
      <div className="rollup-head"><span>Conformance by program</span><BuildProvenance overview={overview} compact /></div>
      <div className="rollup-grid">
        {rollup.map((group) => <button className={`rollup-card${family === group.name ? " active" : ""}`} key={group.name} type="button" onClick={() => setFamily(family === group.name ? "all" : group.name)}>
          <div className="rollup-title"><strong>{group.name}</strong><span>{group.proven}/{group.requirements.length}</span></div>
          <div className="coverage-bar">
            {group.proven > 0 && <span className="bar-verified" style={{ flexGrow: group.proven }} />}
            {group.attention > 0 && <span className="bar-attention" style={{ flexGrow: group.attention }} />}
            {group.unlinked > 0 && <span className="bar-never" style={{ flexGrow: group.unlinked }} />}
            {group.requirements.length - group.proven - group.attention - group.unlinked > 0 && <span className="bar-earlier" style={{ flexGrow: group.requirements.length - group.proven - group.attention - group.unlinked }} />}
          </div>
          <small>{group.proven === 0 ? "nothing proven" : `${group.proven} proven`}{group.unlinked > 0 ? ` · ${group.unlinked} untested` : ""}</small>
        </button>)}
      </div>
    </div>

    <div className="filters test-filters">
      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search a code, title, or rule — e.g. 99454" aria-label="Search charge codes" />
      <select value={family} onChange={(event) => setFamily(event.target.value)} aria-label="Filter by program family">
        <option value="all">All programs</option>
        {families.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
    </div>

    {groups.map((group) => <div className="panel" key={group.name}>
      <div className="panel-title"><h3>{group.name}</h3><span>{group.proven} of {group.requirements.length} proven on this build</span></div>
      <div className="billing-grid">
        {group.requirements.map((item) => {
          const evidence = requirementStatus(item);
          const declared = resolveDeclaredEvidence(item.evidenceTestIds ?? [], tests);
          const unresolved = declared.filter((entry) => !entry.resolved);
          return <article className="code-card" key={item.id}>
            <div className="code-head"><code>{item.code}</code><StatusPill status={evidence} label={requirementStatusLabel[evidence]} /></div>
            <h4>{item.title}</h4>
            <p>{item.rule}</p>
            <div className="code-evidence">
              <StatusPill status={item.certification} />
              <small>{item.linkedTests?.length ? `${item.passingTests ?? 0}/${item.linkedTests.length} linked tests passing` : "No test claims this code"}</small>
            </div>
            {unresolved.length > 0 && <div className="declared-warning">
              <strong>{unresolved.length} reviewed proof{unresolved.length === 1 ? "" : "s"} unreachable</strong>
              <span>The BRD names {unresolved.map((entry) => entry.target).join(", ")}, which no published catalog contains.</span>
            </div>}
            {(item.linkedTests?.length || declared.length > 0) && <details className="code-tests">
              <summary>Show the evidence behind this code</summary>
              {declared.length > 0 && <div className="declared-block">
                <p className="declared-title">Named by the reviewed BRD</p>
                <ul>
                  {declared.map((entry) => <li className={entry.resolved ? "" : "declared-missing"} key={entry.ref}>
                    <StatusPill status={entry.status ?? "missing"} label={entry.resolved ? undefined : "Not published"} />
                    <span><code>{entry.target}</code>{entry.resolved ? <small> · {entry.passing}/{entry.testCount} passing</small> : <small> · no catalog contains this</small>}</span>
                  </li>)}
                </ul>
              </div>}
              {item.linkedTests && item.linkedTests.length > 0 && <div className="declared-block">
                <p className="declared-title">Tests that claim this code</p>
                <ul>
                  {item.linkedTests.map((test) => <li key={test.id}>
                    <StatusPill status={test.status} />
                    <a href={sourceUrl(test.path)} target="_blank" rel="noreferrer">{test.title.split(" › ").at(-1)}</a>
                  </li>)}
                </ul>
              </div>}
            </details>}
            <small className="rule-id">{item.id}</small>
          </article>;
        })}
      </div>
    </div>)}
    {groups.length === 0 && <div className="panel empty">No charge code matches that search.</div>}
  </section>;
}

const UNEXERCISED_LIMIT = 50;

function TestInventoryView({ tests, runs, overview }: { tests: TestEvidence[]; runs: RunSummary[]; overview: Overview }) {
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState("all");
  const exactSha = overview.exactSha;
  // The QA audience cares about the journeys a person can take through the
  // product, which is the Playwright surface. Backend integration tests are
  // real evidence but they are not "what a user does", so they stay out of
  // this view rather than burying it under thousands of unit-level rows.
  const browserTests = useMemo(
    () => tests.filter((test) => (test.testType ?? "playwright") === "playwright"),
    [tests],
  );
  const domains = useMemo(
    () => Array.from(new Set(browserTests.map((test) => test.domain))).sort(),
    [browserTests],
  );
  const summary = useMemo(() => summarizeCoverage(browserTests, exactSha), [browserTests, exactSha]);
  const specs = useMemo(() => groupBySpec(browserTests, exactSha), [browserTests, exactSha]);
  const visible = useMemo(() => filterSpecs(specs, query, domain), [specs, query, domain]);
  const areas = useMemo(() => coverageByDomain(browserTests, exactSha), [browserTests, exactSha]);
  const exercised = visible.filter((spec) => spec.exercised);
  const unexercised = visible.filter((spec) => !spec.exercised);

  return <section>
    <div className="section-heading test-heading">
      <div>
        <p className="eyebrow">Automated coverage</p>
        <h2>What the browser tests actually cover</h2>
        <p className="lede">Each check below drives a real browser against a real API with synthetic data. This page separates the two questions that matter: <strong>what did CI run on this build</strong>, and <strong>what does CI never run at all</strong>. A test nobody runs is a coverage gap, not a failure.</p>
      </div>
      <BuildProvenance overview={overview} />
    </div>

    <div className="workflow-summary">
      <div className="summary-card summary-proven"><span>Verified on this build</span><strong>{summary.verified}</strong><small>of {summary.tests} browser tests</small></div>
      <div className="summary-card summary-attention"><span>Failing or flaky</span><strong>{summary.failing}</strong><small>need a look now</small></div>
      <div className="summary-card summary-gap"><span>Verified on an older build</span><strong>{summary.earlier}</strong><small>real evidence, not current</small></div>
      <div className="summary-card summary-gap"><span>Never run in CI</span><strong>{summary.never}</strong><small>in the repo, no selection runs them</small></div>
    </div>

    <div className="coverage-callout">
      <div>
        <span>The honest headline</span>
        <p><strong>{summary.specsExercised} of {summary.specs} browser spec files</strong> have ever produced a result, and <strong>{summary.verified} of {summary.tests} tests</strong> carry a verdict for this build. Everything else exists in the repository but no CI selection executes it.</p>
      </div>
      <div className="coverage-areas">
        {areas.map((area) => <div className="coverage-area" key={area.name}>
          <div><span>{area.name}</span><small>{area.verified}/{area.tests}</small></div>
          <div className="area-track"><span style={{ width: `${Math.round((area.verified / area.tests) * 100)}%` }} /></div>
        </div>)}
      </div>
    </div>

    <div className="filters test-filters">
      <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search a feature or a test — e.g. claims, login, export" aria-label="Search tests" />
      <select value={domain} onChange={(event) => setDomain(event.target.value)} aria-label="Filter by area">
        <option value="all">All areas</option>
        {domains.map((name) => <option key={name} value={name}>{name}</option>)}
      </select>
    </div>

    <div className="panel-title spec-section-title"><h3>Specs CI runs</h3><span>{exercised.length} of {visible.length} matching spec files</span></div>
    <div className="panel spec-list">
      {exercised.map((spec) => <details className={`spec-row spec-row-${spec.status}`} key={spec.path}>
        <summary>
          <div className="spec-main">
            <div className="spec-context"><span>{spec.domain}</span><span>{spec.tests.length} test{spec.tests.length === 1 ? "" : "s"}</span>{spec.durationMs > 0 && <span>{(spec.durationMs / 1000).toFixed(1)}s</span>}</div>
            <h3>{spec.name}</h3>
            <CoverageBar spec={spec} />
            <div className="spec-tally">
              {spec.verified > 0 && <b className="tally-verified">{spec.verified} verified now</b>}
              {spec.earlier > 0 && <b className="tally-earlier">{spec.earlier} on an older build</b>}
              {spec.never > 0 && <b className="tally-never">{spec.never} never run</b>}
              {spec.excluded > 0 && <b className="tally-excluded">{spec.excluded} excluded</b>}
            </div>
          </div>
          <div className="spec-result"><StatusPill status={spec.status} label={spec.status === "missing" ? "Never run" : undefined} /></div>
        </summary>
        <div className="spec-detail">
          {spec.tests.map((test) => <TestDetail key={test.id} test={test} runs={runs} exactSha={exactSha} />)}
        </div>
      </details>)}
      {exercised.length === 0 && <p className="empty">No spec CI runs matches that search.</p>}
    </div>

    <details className="panel gap-panel" open={exercised.length === 0}>
      <summary>
        <div>
          <strong>Specs no CI run exercises</strong>
          <small>{unexercised.length} spec file{unexercised.length === 1 ? "" : "s"} · {unexercised.reduce((total, spec) => total + spec.tests.length, 0)} tests written but never executed</small>
        </div>
        <span className="gap-chevron" aria-hidden="true">▾</span>
      </summary>
      <p className="gap-note">These are written and committed but sit outside every CI selection, so they prove nothing about any build. Treat this as the browser-coverage backlog, not as breakage.</p>
      <ul className="gap-list">
        {unexercised.slice(0, UNEXERCISED_LIMIT).map((spec) => <li key={spec.path}>
          <a href={sourceUrl(spec.path)} target="_blank" rel="noreferrer">{spec.name}</a>
          <span>{spec.domain}</span>
          <small>{spec.tests.length} test{spec.tests.length === 1 ? "" : "s"}</small>
        </li>)}
      </ul>
      {unexercised.length > UNEXERCISED_LIMIT && <p className="gap-note">Showing {UNEXERCISED_LIMIT} of {unexercised.length}. Narrow the search to see the rest.</p>}
      {unexercised.length === 0 && <p className="gap-note">Every matching spec file has produced a result at least once.</p>}
    </details>
  </section>;
}

function RequestView() {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setSubmitting(true); setMessage("");
    const form = new FormData(event.currentTarget);
    const payload = Object.fromEntries(form.entries());
    try {
      const response = await api<{ key: string }>("/test-requests", { method: "POST", body: JSON.stringify(payload) });
      setMessage(`Created ${response.key} in Plane Backlog for review.`);
      event.currentTarget.reset();
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not create request."); }
    finally { setSubmitting(false); }
  }
  return <section><div className="section-heading"><div><p className="eyebrow">Improve billing assurance</p><h2>Describe the billing outcome that is wrong or missing</h2></div><p className="lede">This creates a reviewed Plane ticket for synthetic test work. It cannot edit code automatically and must never contain patient data.</p></div>
    <form className="panel form" onSubmit={submit}>
      <input type="hidden" name="surface" value="billing engine" /><input type="hidden" name="priority" value="high" /><input type="hidden" name="billingCritical" value="true" />
      <label>Billing scenario<input name="flow" required maxLength={120} placeholder="e.g. 15 automatic BP days should select 99445" /></label>
      <label>Expected charge behavior<textarea name="expected" required maxLength={1200} placeholder="Given synthetic facts… when claims are generated… then this code is charged exactly once…" /></label>
      <label>Charge code / requirement ID<input name="requirementId" required maxLength={80} placeholder="e.g. BILL-RPM-99454" /></label>
      <label>Synthetic acceptance notes<textarea name="notes" maxLength={1200} placeholder="Include thresholds, the wrong code to exclude, and the export fields to reconcile." /></label>
      <button className="primary" disabled={submitting}>{submitting ? "Creating…" : "Create reviewed billing-test ticket"}</button>
      {message && <p className="form-message">{message}</p>}
    </form>
  </section>;
}

export default function App() {
  const [view, setView] = useState<View>("home");
  const [authenticated, setAuthenticated] = useState(Boolean(token()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState<Overview>(emptyOverview);
  const [tests, setTests] = useState<TestEvidence[]>([]);
  const [journeys, setJourneys] = useState<BusinessJourney[]>([]);
  const [requirements, setRequirements] = useState<BillingRequirement[]>([]);
  const [flows, setFlows] = useState<BillingFlow[]>([]);
  const [billingApproval, setBillingApproval] = useState<BillingApproval>({ status: "review-required", owners: ["CMS Billing Specialist", "Product Owner"] });

  const refresh = useCallback(async () => {
    if (!token()) return;
    setLoading(true); setError("");
    try {
      const [overviewData, journeyData, testData, requirementData, flowData] = await Promise.all([
        api<Overview>("/overview"), api<{ items: BusinessJourney[] }>("/journeys"), api<{ items: TestEvidence[] }>("/tests"), api<{ approval: BillingApproval; items: BillingRequirement[] }>("/requirements/billing"),
        // Reference documentation is additive: a Hub that has not received it
        // yet must still render every evidence screen.
        api<{ items: BillingFlow[] }>("/flows").catch(() => ({ items: [] as BillingFlow[] })),
      ]);
      setOverview(overviewData); setJourneys(journeyData.items); setTests(testData.items); setRequirements(requirementData.items); setBillingApproval(requirementData.approval); setFlows(flowData.items);
    } catch (cause) {
      if (cause instanceof Error && cause.message === "AUTH_REQUIRED") setAuthenticated(false);
      else setError(cause instanceof Error ? cause.message : "Could not load billing evidence.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const code = new URLSearchParams(location.search).get("code");
    if (code) finishSignIn(code).then(() => { setAuthenticated(true); return refresh(); }).catch((cause) => setError(cause instanceof Error ? cause.message : "Sign-in failed."));
    else if (authenticated) void refresh();
  }, [authenticated, refresh]);

  if (!authenticated) return <main className="login"><div className="login-card"><div className="brand-mark">B</div><p className="eyebrow">BettrAI internal</p><h1>Billing QA</h1><p>One clear answer: did qualifying patient activity produce the correct charge?</p><button className="primary" onClick={() => void signIn()}>Sign in</button>{error && <p className="error">{error}</p>}<small>Invite-only · synthetic data only · activity audited</small></div></main>;

  return <div className="shell"><aside><div className="brand"><div className="brand-mark">B</div><div><strong>Billing QA</strong><span>BettrAI</span></div></div><nav className="side-nav"><div className="nav-group"><p>Focused workspace</p>{(Object.keys(viewMeta) as View[]).map((id) => <button className={view === id ? "active" : ""} key={id} onClick={() => setView(id)}><span className="nav-indicator" /><span><strong>{viewMeta[id].label}</strong><small>{viewMeta[id].hint}</small></span></button>)}</div></nav><div className="side-footer"><span className="environment">Billing evidence only</span><button onClick={() => void signOut()}>Sign out</button></div></aside><main><header><div className="header-context"><span>System-critical workflow</span><strong>{viewMeta[view].label}</strong></div><div className="header-actions"><BuildProvenance overview={overview} /><button className="secondary" onClick={() => void refresh()}>Refresh</button></div></header>{loading && <div className="loading">Refreshing billing evidence…</div>}{error && <p className="error banner">{error}</p>}<div className="content">
    {view === "home" && <HomeView requirements={requirements} tests={tests} journeys={journeys} overview={overview} onOpen={setView} />}
    {view === "flows" && <HowBillingWorksView flows={flows} requirements={requirements} onOpenCodes={() => setView("codes")} />}
    {view === "assurance" && <BillingAssuranceView journeys={journeys} tests={tests} requirements={requirements} runs={overview.recentRuns} overview={overview} onRequestTest={() => setView("request")} />}
    {view === "codes" && <ChargeCodeBoardView requirements={requirements} approval={billingApproval} tests={tests} overview={overview} />}
    {view === "tests" && <TestInventoryView tests={tests} runs={overview.recentRuns} overview={overview} />}
    {view === "request" && <RequestView />}
  </div></main></div>;
}
